import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { IconButton, InputAdornment, Link, Stack } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { authAPI } from '../../shared/api/AuthAPI'
import { clearLastKnownLocation, loginUser } from '../../store/actions'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import FsAuthWrapper from './auth-wrapper'
import { validateEmail } from '../../shared/utils'
import LoadingDash from '../common/atoms/LoadingDash'

const FsSignIn = () => {
  const navigate = useNavigate()
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState<string | null>(null)
  const [password, setPassword] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { currentLanguage, lastKnownLocation } = useAppSelector((state) => state.currentUser)

  const handleLogin = async () => {
    if (email && password) {
      try {
        setLoading(true)
        const startTime = Date.now()

        const user = await authAPI.authorize(email.toLowerCase(), password)
        dispatch(loginUser(user.access, user.refresh, user.company, user.user))
        const elapsedTime = Date.now() - startTime
        const minLoadTime = 1500

        setTimeout(() => {
          if (lastKnownLocation) {
            const location = lastKnownLocation
            dispatch(clearLastKnownLocation())
            navigate(location)
          } else if (
            user.user.profile.appAccess?.length === 1 &&
            user.user.profile.appAccess.includes('fs')
          ) {
            navigate('/fs/dashboard')
          } else if (
            user.user.profile.appAccess?.length === 1 &&
            user.user.profile.appAccess.includes('pt')
          ) {
            navigate('/pt/home')
          } else {
            navigate('/ines/home')
          }
          setLoading(false)
        }, Math.max(0, minLoadTime - elapsedTime))
      } catch (e: any) {
        if (e.response.status === 401) {
          setMessage('incorrect.credentials')
          setLoading(false)
        } else {
          setMessage('something.went.wrong')
          setLoading(false)
        }
      }
    }
  }

  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault()
        if (!loading && email && password && validateEmail(email ?? '')) handleLogin()
      }
    }
    document.addEventListener('keydown', listener)
    return () => {
      document.removeEventListener('keydown', listener)
    }
  }, [email, password])

  useEffect(() => {
    if (lastKnownLocation) {
      setMessage('token.expired')
    }
  }, [lastKnownLocation])

  return (
    <FsAuthWrapper>
      <Box
        sx={{
          marginY: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '80%',
          transition: 'filter 0.3s ease-in-out',
          filter: loading ? 'blur(8px)' : 'none',
        }}
      >
        {message && (
          <Typography color="error" textAlign="center" mt={1} mb={2}>
            {`${t(message as any)} `}
            <Link href="mailto:support@ines-analytics.com" color="error">
              {currentLanguage === 'de' ? 'Kontaktieren Sie uns' : 'Contact us'}
            </Link>
          </Typography>
        )}
        <Typography variant="h1" color="primary">
          {t('welcome.title')}
        </Typography>
        <Typography textAlign="center" my={2}>
          {t('login.desc')}
        </Typography>
        <Box sx={{ width: 350, maxWidth: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            size="small"
            id="email"
            label={t('email.address')}
            name="email"
            autoComplete="email"
            autoFocus
            error={email !== null && !validateEmail(email)}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            sx={{ borderRadius: 1 }}
          />
          {email !== null && !validateEmail(email) && (
            <Typography ml={1} color="error" variant="body2">
              {t('email.error')}
            </Typography>
          )}
          <TextField
            margin="normal"
            required
            size="small"
            fullWidth
            name="password"
            label={t('password')}
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            error={password !== null && !password}
            onChange={(event) => setPassword(event.target.value)}
            sx={{ borderRadius: 1 }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          {password !== null && !password && (
            <Typography ml={1} color="error" variant="body2">
              {t('password.error')}
            </Typography>
          )}
          <Stack alignItems="flex-end">
            <Button
              onClick={() => navigate('/forgot-password')}
              size="small"
              sx={{
                textTransform: 'none',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <Typography align="center" variant="body2">
                {t('forgot.password')}
              </Typography>
            </Button>
          </Stack>
          <Button
            type="submit"
            disabled={loading || !validateEmail(email ?? '') || !password}
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            onClick={handleLogin}
          >
            {t('sign.in')}
          </Button>
        </Box>
        <Typography textAlign="center" variant="body2">
          {`${t('login.issue')} `}
          <Link href="mailto:support@ines-analytics.com">
            {currentLanguage === 'de' ? 'Kontaktieren Sie uns' : 'Contact us'}
          </Link>
        </Typography>
      </Box>
      {loading && (
        <Box sx={{ position: 'absolute' }}>
          <LoadingDash title={t('sign.in.loading')} />
        </Box>
      )}
    </FsAuthWrapper>
  )
}

export default FsSignIn
